import * as React from "react";
import {DraftBlockType} from "draft-js";
import * as Color from "shared/util/Color";
import {DraftStyles, AnchorTarget} from "./models";
import {Glyph, GlyphButton, PropertyRowComponent} from
        "../../../../../editor/ui/experience/widgets/toolbox/PropertiesView";
import {NumericInputComponent, CreatableSelect} from "@zmags/zmags-ui-library";
import SelectComponent, {SelectOption} from "shared/ui/common/SelectComponent";
import {PropertyContainerComponent} from
        "../../../../../editor/ui/experience/widgets/toolbox/PropertyContainerComponent";
import {ColorPicker} from "shared/ui/common/ColorPicker";
import {InputActionMeta} from "react-select";
import {FontFaceVariants} from "../../fonts/WebFontsUtil";
import {FontDescriptor} from "../../../../data/fonts/FontDescriptor";
import {FontFamily} from "shared/xp/models/widgets";
import InputComponent from "shared/ui/common/InputComponent";
import { Button, Col, Row } from "react-bootstrap";
interface FontOption extends SelectOption {
    fontDescriptor: FontDescriptor;
}

interface FontOptions {
    label: string;
    options: FontOption[];
}

const enum FontStyles {
    NORMAL = "normal",
    ITALIC = "italic",
}

type Indeterminate = "unknown";

const enum Alignment {
    LEFT = "left",
    RIGHT = "right",
    CENTER = "center"
}

const enum ChangeSource {
    Decoration,
    Color,
    Alignment,
    LetterSpacing,
    FontSize,
    FontStyle,
    LineHeight,
    BlockType
}

/**
 * CreatableSelect "emotion-style" like styles
 *
 * Documentation and examles are here:
 * https://react-select.com/styles
 */
const creatableStyles = {
    control: (providedStyles) => ({
        ...providedStyles,
        backgroundColor: "#333333",
        color: "white",
        borderRadius: 0,
        border: "none",
        width: "80px"
    }),
    input: (providedStyles) => ({
        ...providedStyles,
        color: "white",
    }),
    singleValue: (providedStyles) => ({
        ...providedStyles,
        color: "white",
    }),
    indicatorsContainer: (providedStyles) => ({
        ...providedStyles,
        color: "#C6C6C6",
    }),
    menu: (providedStyles) => ({
        ...providedStyles,
        width: "80px",
        borderRadius: "0px",
        marginTop: "0px",
        marginBottom: "0px",
    }),
    menuList: (providedStyles) => ({
        ...providedStyles,
        marginTop: "0px",
        marginBottom: "0px",
    }),
    option: (providedStyles, props) => ({
        ...providedStyles,
        paddingTop: "0px",
        paddingBottom: "0px",
        color: "#000000",
        backgroundColor: props.data.__isNew__ && "#DEEBFF"
    })
};

const textEditorDefaults: Partial<TextEditorControlsProps> = {
    fontWeight: "400",
    fontStyle: FontStyles.NORMAL,
    alignment: Alignment.LEFT,
    blockType: "unstyled",
    fontColor: {
        a: 1,
        b: 0,
        g: 0,
        r: 0,
    },
    lineHeight: 1.4,
    letterSpacing: 0,
    fontSize: 14,
    textDecorations: [],
};

enum SelectActions {
    INPUTCHANGE = "input-change",
    MENUCLOSE = "menu-close",
}

type AlignmentStates = Alignment | Indeterminate;

interface TextEditorControlsProps {
    onChange: (propsDelta: Partial<TextEditorControlsProps>, changeSource: ChangeSource) => void;
    onMouseDown: () => void;
    fontWeight?: string;
    fontStyle?: string;
    fontSize?: number;
    fontColor?: Color.RGBA;
    fontProvider?: FontFamily;
    alignment?: AlignmentStates;
    letterSpacing?: number;
    lineHeight?: number;
    blockType?: DraftBlockType;
    textDecorations?: Array<DraftStyles>;
    fontStyleOptions: Array<FontFaceVariants>;
    fontFamilyOptions: FontOptions[];
    hasAction: boolean;
    anchorUrl: string;
    anchorTarget: AnchorTarget;
    hasSelection: boolean;
    selectionHasLink: boolean;
    onFontFamilyInputChange: (value: string) => void;
    onToggleFontFamily: (fontFamilyName: string) => void;
    onAnchorAttrsApply: (url: string, target: string) => void;
    handleAnchorUrlChange: (anchorUrl: string) => void;
    handleAnchorTargetChange: (target: AnchorTarget) => void;
    isEmailMode: boolean
}

interface TextEditorControlsState {
    blockTypeOptions: SelectOption<DraftBlockType>[];
    fontFaceSearchTerm: string;
    anchorTargetOptions: SelectOption[];
}

const fontSizeOptions = [
    { value: "8", label: "8" },
    { value: "9", label: "9" },
    { value: "10", label: "10" },
    { value: "11", label: "11" },
    { value: "12", label: "12" },
    { value: "14", label: "14" },
    { value: "18", label: "18" },
    { value: "24", label: "24" },
    { value: "30", label: "30" },
    { value: "36", label: "36" },
    { value: "48", label: "48" },
    { value: "60", label: "60" },
    { value: "72", label: "72" },
    { value: "96", label: "96" }
];

class TextEditorControls extends React.Component<TextEditorControlsProps, TextEditorControlsState> {
    static defaultProps: TextEditorControlsProps = {
        ...textEditorDefaults,
        onChange: () => undefined,
        onMouseDown: () => undefined,
        onFontFamilyInputChange: () => undefined,
        onToggleFontFamily: () => undefined,
        onAnchorAttrsApply: () => undefined,
        fontStyleOptions: [],
        fontFamilyOptions: [],
        hasAction: false,
        handleAnchorUrlChange: () => undefined,
        handleAnchorTargetChange: () => undefined,
        anchorUrl: "",
        anchorTarget: AnchorTarget.self,
        hasSelection: false,
        selectionHasLink: false,
        isEmailMode: false
    };

    constructor(props: TextEditorControlsProps) {
        super(props);

        this.state = {
            fontFaceSearchTerm: "",
            blockTypeOptions: [
                {label: "Default", value: textEditorDefaults.blockType},
                {label: "Heading 1", value: "header-one"},
                {label: "Heading 2", value: "header-two"},
                {label: "Heading 3", value: "header-three"},
                {label: "Heading 4", value: "header-four"},
                {label: "Heading 5", value: "header-five"},
                {label: "Heading 6", value: "header-six"},
            ],
            anchorTargetOptions: [
                {label: "Current window/tab", value: "_self"},
                {label: "Next window/tab", value: "_blank"}
            ],
        };
    }

    handleFontFamilyInputChange = (value: string, actionMeta: InputActionMeta) => {
        const {action} = actionMeta;
        if (action === SelectActions.INPUTCHANGE || action === SelectActions.MENUCLOSE) {
            const term = action === SelectActions.INPUTCHANGE ? value : "";
            this.setState({fontFaceSearchTerm: term});
            this.props.onFontFamilyInputChange(term);
        }
    }

    toggleFontFamily = (e: SelectOption) => {
        const {fontProvider, onToggleFontFamily} = this.props;
        const targetFontFamily = e.value;

        if (!fontProvider || fontProvider.fontFamily !== targetFontFamily) {
            onToggleFontFamily(targetFontFamily);
        }
    }

    // This ugly method exists because I can't just spread this.props because it's type includes React specific props.
    getPartialProps = (): Partial<TextEditorControlsProps> => {
        const {
            fontWeight,
            fontStyle,
            textDecorations,
            fontSize,
            fontProvider,
            alignment,
            lineHeight,
            blockType,
            letterSpacing,
            fontColor
        } = this.props;

        return {
            fontWeight,
            fontStyle,
            textDecorations,
            fontSize,
            fontProvider,
            alignment,
            lineHeight,
            blockType,
            letterSpacing,
            fontColor
        };
    }

    toggleColor = (fontColor: Color.RGBA) => {
        const nextProps: Partial<TextEditorControlsProps> = {
            fontColor
        };

        this.onChange(ChangeSource.Color, nextProps);
    }

    toggleFontStyle = (fontFaceVariant: FontFaceVariants) => {
        const {style: nextFontStyle, weight: nextFontWeight} = fontFaceVariant;
        const {fontStyle: currentFontStyle, fontWeight: currentFontWeight} = this.props;

        const fontWeight = currentFontWeight !== nextFontWeight ? nextFontWeight : currentFontWeight;
        const fontStyle = currentFontStyle !== nextFontStyle ? nextFontStyle : currentFontStyle;

        const nextProps: Partial<TextEditorControlsProps> = {
            fontWeight,
            fontStyle,
        };

        return this.onChange(ChangeSource.FontStyle, nextProps);
    }

    toggleLetterSpacing = (letterSpacing: number) => {
        const nextProps: Partial<TextEditorControlsProps> = {
            letterSpacing
        };

        this.onChange(ChangeSource.LetterSpacing, nextProps);
    }

    toggleFontSize = (fontSize: number) => {
        const nextProps: Partial<TextEditorControlsProps> = {
            fontSize
        };

        this.onChange(ChangeSource.FontSize, nextProps);
    }

    toggleBlockType = (blockType: DraftBlockType) => {
        const nextProps: Partial<TextEditorControlsProps> = {
            blockType
        };

        this.onChange(ChangeSource.BlockType, nextProps);
    }

    toggleAlign = (alignment: AlignmentStates) => {
        const nextProps: Partial<TextEditorControlsProps> = {
            alignment
        };

        this.onChange(ChangeSource.Alignment, nextProps);
    }

    toggleLineHeight = (lineHeight: number) => {
        const nextProps: Partial<TextEditorControlsProps> = {
            lineHeight
        };

        this.onChange(ChangeSource.LineHeight, nextProps);
    }

    removeStyle = (style: DraftStyles, textDecorations: Array<DraftStyles>) => {
        return textDecorations.filter((currentStyleItem) => currentStyleItem !== style);
    }

    toggleStyle = (style: DraftStyles) => {
        let {textDecorations} = this.props;
        if(textDecorations.indexOf(style) === -1) {
            // Both caps can not be applied to a text selection
            if(style === DraftStyles.SMALLCAPS) {
                textDecorations = this.removeStyle(DraftStyles.CAPITAL, textDecorations);
            } else if(style === DraftStyles.CAPITAL) {
                textDecorations = this.removeStyle(DraftStyles.SMALLCAPS, textDecorations);
            }
            textDecorations.push(style);
        } else {
            textDecorations = textDecorations.filter((currentStyleItem) => currentStyleItem !== style);
        }

        const nextProps: Partial<TextEditorControlsProps> = {
            textDecorations
        };

        this.onChange(ChangeSource.Decoration, nextProps);
    }

    getFontFamilyOption = (targetFontFamily: string): FontOption => {
        const {fontFamilyOptions} = this.props;
        let targetFontOption = undefined;
        fontFamilyOptions.filter((item) => !!item).forEach(
            (fontGroup) => {
                if(!targetFontOption) {
                    targetFontOption = fontGroup.options.find(
                        (fontOptionItem) => fontOptionItem.value === targetFontFamily);
                }
            }
        );

        return targetFontOption;
    }

    getDefaultFontFamilyOption = (fontFamilyOptions: FontOptions[]): FontOption => {
        let selectedFontOption = undefined;
        // Choose the default font from either the most recent selection or first standard font.
        fontFamilyOptions.filter((item) => !!item).forEach((fontOption) => {
            const label = fontOption.label.toLowerCase();
            if(label === "recents" || (!selectedFontOption && label === "standard fonts")) {
                selectedFontOption = fontOption.options[0];
            }
        });

        return selectedFontOption;
    }

    onChange = (changeSource: ChangeSource, propsDelta: Partial<TextEditorControlsProps>) => {
        const partialProps = this.getPartialProps();
        const nextProps = {
            ...partialProps,
            ...propsDelta,
        };
        this.props.onChange(nextProps, changeSource);
    }

    onAnchorAttrsApply = () => {
        const {anchorUrl, anchorTarget, onAnchorAttrsApply} = this.props;
        onAnchorAttrsApply(anchorUrl, anchorTarget);
    }

    onAnchorUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.props.handleAnchorUrlChange(e.target.value);
    }

    onAnchorTargetChange = (option: SelectOption<AnchorTarget>) => {
        this.props.handleAnchorTargetChange(option.value);
    }

    render() {
        const {
            blockTypeOptions,
            fontFaceSearchTerm,
            anchorTargetOptions
        } = this.state;

        const {
            fontStyle,
            fontWeight,
            alignment,
            letterSpacing,
            lineHeight,
            fontSize,
            fontColor,
            blockType,
            textDecorations,
            onMouseDown,
            fontStyleOptions,
            fontFamilyOptions,
            fontProvider,
            hasAction,
            anchorUrl,
            anchorTarget,
            hasSelection,
            selectionHasLink,
            isEmailMode
        } = this.props;

        const targetFontOption = fontProvider ?
            this.getFontFamilyOption(fontProvider.fontFamily) || this.getDefaultFontFamilyOption(fontFamilyOptions)
            : null;

        const groupStyles: React.CSSProperties = {
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
        };
        const groupBadgeStyles: React.CSSProperties = {
            backgroundColor: "#EBECF0",
            borderRadius: "2em",
            color: "#172B4D",
            display: "inline-block",
            fontSize: 12,
            fontWeight: "normal",
            lineHeight: "1",
            minWidth: 1,
            padding: "0.16666666666667em 0.5em",
            textAlign: "center",
        };

        const formatGroupLabel = (data) => (
            <div style={groupStyles}>
                <span>{data.label}</span>
                <span style={groupBadgeStyles}>{data.options.length}</span>
            </div>
        );

        const selectedFontStyle = fontStyleOptions.find((fontStyleOption) =>
            fontStyleOption.style === fontStyle && fontStyleOption.weight === fontWeight);

        const currentAnchorTargetOption = anchorTargetOptions.find(({value}) => value === anchorTarget) || anchorTargetOptions[0];

        return (
            <div>
                <PropertyContainerComponent
                    widgetSelected={true}
                    label={"Text"}
                    name={"character"}
                    animateExpandOnMount={true}
                >
                    {
                        /*
                            Normally this would be surrounded in a Grid element, but that breaks styling
                        */
                    }
                    <PropertyRowComponent
                        name={"font"}
                        label={"Font"}
                        tip={"Search all available fonts including Google Fonts not shown in the list"}
                    >
                        <SelectComponent
                            // The typings for this library are out of date and don't work for grouped data.
                            // @ts-ignore
                            onChange={this.toggleFontFamily}
                            onInputChange={this.handleFontFamilyInputChange}
                            inputValue={fontFaceSearchTerm}
                            options={fontFamilyOptions}
                            // @ts-ignore
                            value={targetFontOption}
                            placeholder={"Select a font"}
                            formatGroupLabel={formatGroupLabel}
                            onMenuOpen={onMouseDown}
                            useOptionFontStyle={true}
                        />
                    </PropertyRowComponent>
                    <PropertyRowComponent name={"style"} label={"Style"}>
                        <SelectComponent
                            onChange={this.toggleFontStyle}
                            // no reason to show style options list if there is no selected font-family
                            options={fontProvider ? fontStyleOptions : []}
                            value={selectedFontStyle}
                            placeholder={"Select style"}
                            onMenuOpen={onMouseDown}
                        />
                    </PropertyRowComponent>
                    <PropertyRowComponent name={"decoration"}>
                        {/* This hidden input actually has styling associated with it... */}
                        <input type="hidden" className="text-decoration" />
                        <GlyphButton
                            glyphicon={"text-color"}
                            styleProperty={"text-decoration"}
                            changeStyleProperty={"text-decoration"}
                            title={"Underline"}
                            value={"underline"}
                            selected={textDecorations.indexOf(DraftStyles.UNDERLINE) !== -1}
                            onClick={() => this.toggleStyle(DraftStyles.UNDERLINE)}
                        />
                        <GlyphButton
                            glyphicon={"font"}
                            styleProperty={"text-decoration"}
                            changeStyleProperty={"text-decoration"}
                            title={"Line-Through"}
                            value={"line-through"}
                            selected={textDecorations.indexOf(DraftStyles.STRIKETHROUGH) !== -1}
                            onClick={() => this.toggleStyle(DraftStyles.STRIKETHROUGH)}

                        />
                        <GlyphButton
                            glyphicon={"text-shadow"}
                            styleProperty={"text-decoration"}
                            changeStyleProperty={"text-shadow"}
                            title={"Test Shadow"}
                            selected={textDecorations.indexOf(DraftStyles.SHADOW) !== -1}
                            onClick={() => this.toggleStyle(DraftStyles.SHADOW)}
                        />
                    </PropertyRowComponent>
                    <PropertyRowComponent name={"style"} label={"Case"}>
                        <GlyphButton
                            styleProperty={"text-transform-uppercase"}
                            changeStyleProperty={"text-case"}
                            title={"Capitalize"}
                            style={{textTransform: "uppercase"}}
                            label={"ABC"}
                            selected={textDecorations.indexOf(DraftStyles.CAPITAL) !== -1}
                            onClick={() => this.toggleStyle(DraftStyles.CAPITAL)}
                        />
                        <GlyphButton
                            styleProperty={"font-variant-smallcaps"}
                            changeStyleProperty={"text-case"}
                            title={"Small Caps"}
                            style={{fontVariant: "small-caps"}}
                            label={"abc"}
                            selected={textDecorations.indexOf(DraftStyles.SMALLCAPS) !== -1}
                            onClick={() => this.toggleStyle(DraftStyles.SMALLCAPS)}
                        />
                    </PropertyRowComponent>
                    <PropertyRowComponent name={"size"} label={"Size"}>
                        <div style={{display: "inline-block"}}>
                            <CreatableSelect
                                value={fontSize}
                                defaultValue={textEditorDefaults.fontSize}
                                onChange={this.toggleFontSize}
                                label="px"
                                styles={creatableStyles}
                                options={fontSizeOptions}
                            />
                        </div>
                        <Glyph glyphicon={"text-size"} onRight={true}/>
                    </PropertyRowComponent>
                    <PropertyRowComponent name={"color"} label={"Color"}>
                        <ColorPicker
                            color={fontColor}
                            onChange={this.toggleColor}
                            showInput={false}
                        />
                    </PropertyRowComponent>
                    <PropertyRowComponent name={"line-height"} label={"Line Height"}>
                        <NumericInputComponent
                            classNames="line-height small"
                            wrapperClassNames="pull-left"
                            onChange={this.toggleLineHeight}
                            value={lineHeight}
                            defaultValue={textEditorDefaults.lineHeight}
                            disabled={false}
                            step={0.1}
                            min={0.1}
                            max={100}
                            title={"percentage of the font size"}
                            debounceDelay={500}
                        />
                        <Glyph glyphicon={"text-height"} onRight={true}/>
                    </PropertyRowComponent>
                    <PropertyRowComponent name={"letter-spacing"} label={"Letter Spacing"}>
                        <NumericInputComponent
                            units="px"
                            defaultValue={textEditorDefaults.letterSpacing}
                            classNames="font-letter-spacing small"
                            wrapperClassNames="pull-left"
                            min={-30}
                            onChange={this.toggleLetterSpacing}
                            value={letterSpacing}
                            debounceDelay={500}
                        />
                        <Glyph glyphicon={"text-size"} onRight={true}/>
                    </PropertyRowComponent>
                    <PropertyRowComponent name={"alignment"} label={"Alignment"}>
                        <GlyphButton
                            changeStyleProperty={"text-align"}
                            title={"Left"}
                            glyphicon={"align-left"}
                            onClick={() => this.toggleAlign(Alignment.LEFT)}
                            selected={alignment === Alignment.LEFT}
                        />
                        <GlyphButton
                            changeStyleProperty={"text-align"}
                            title={"Center"}
                            glyphicon={"align-center"}
                            onClick={() => this.toggleAlign(Alignment.CENTER)}
                            selected={alignment === Alignment.CENTER}
                        />
                        <GlyphButton
                            changeStyleProperty={"text-align"}
                            title={"Right"}
                            glyphicon={"align-right"}
                            onClick={() => this.toggleAlign(Alignment.RIGHT)}
                            selected={alignment === Alignment.RIGHT}
                        />
                    </PropertyRowComponent>
                    <PropertyRowComponent
                        name={"html-tag"}
                        label={"HTML Tag"}
                        tip={"Used for SEO purposes and won't affect the appearance"}
                    >
                        <SelectComponent
                            value={blockTypeOptions.find((el => el.value === blockType))}
                            options={blockTypeOptions}
                            classNames={"form-control"}
                            disabled={false}
                            onChange={
                                (val) => {
                                    const blockValue = val.value as DraftBlockType;
                                    this.toggleBlockType(blockValue);
                                }
                            }
                        />
                    </PropertyRowComponent>
                    <PropertyRowComponent
                        name={"hyperlink-target"}
                        label={"Hyperlink"}
                        tip={
                            isEmailMode
                                ? "This feature cannot be used in email mode."
                                : "Select text to add a hyperlink. Replaces any selected links"
                        }
                    >
                        <SelectComponent
                            disabled = {isEmailMode}
                            value={currentAnchorTargetOption}
                            options={anchorTargetOptions}
                            classNames={"form-control"}
                            onChange={this.onAnchorTargetChange}
                        />
                    </PropertyRowComponent>
                    <Row className="hyperlink-url-section properties" componentClass="section">
                        <Col md={8}>
                            <InputComponent
                                disabled={isEmailMode}
                                type="text"
                                placeholder="https://example.com"
                                units={null}
                                classNames="full-width"
                                value={!isEmailMode ? anchorUrl : ''}
                                onChange={this.onAnchorUrlChange}
                            />
                        </Col>
                        <Col md={4}>
                            <Button
                                size="sm"
                                block={true}
                                onClick={this.onAnchorAttrsApply}
                                disabled={hasAction || !hasSelection || isEmailMode || (!selectionHasLink && anchorUrl.length === 0)}
                            >
                                Apply
                            </Button>
                        </Col>
                    </Row>
                </PropertyContainerComponent>
            </div>
        );
    }
}

export {
    TextEditorControls as default,
    TextEditorControls,
    Alignment,
    AlignmentStates,
    TextEditorControlsProps,
    ChangeSource,
    textEditorDefaults,
    FontOptions,
    FontOption,
};



// WEBPACK FOOTER //
// ./src/shared/ui/experience/widgets/textEnhancedWidget/TextEditorControls.tsx